import React, { useMemo, useState } from "react";
import { mockGenerateProject } from "../../../mockEngine/mockGenerateProject";
import { applyMockScoring } from "../../../mockEngine/mockScoring";

export default function MockEngineViewer({
  durationMonths = 12,
  onDurationChange,
  typologyId = "TYP-BLD-01",
  startDate = "2026-01-01"
}) {
  const [localTypologyId, setLocalTypologyId] = useState(typologyId);
  const [localStartDate, setLocalStartDate] = useState(startDate);

  const project = useMemo(() => {
    const base = mockGenerateProject({
      typologyId: localTypologyId,
      projectId: "PRJ-MOCK-001",
      projectName: "Mock — Visual Test",
      startDate: localStartDate,
      durationMonths: Number(durationMonths),
    });

    return applyMockScoring(base);
  }, [localTypologyId, localStartDate, durationMonths]);

  const k = project?.kpis;

  return (
    <div style={{ padding: 16, display: "grid", gap: 16 }}>
      {/* Controls */}
      <section style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end" }}>
          <div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Typology ID</div>
            <input
              value={localTypologyId}
              onChange={(e) => setLocalTypologyId(e.target.value)}
              style={{ padding: 8, width: 240 }}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Start date (YYYY-MM-DD)</div>
            <input
              value={localStartDate}
              onChange={(e) => setLocalStartDate(e.target.value)}
              style={{ padding: 8, width: 170 }}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Duration (months)</div>
            <input
              type="number"
              value={durationMonths}
              min={1}
              onChange={(e) => onDurationChange(Number(e.target.value))}
              style={{ padding: 8, width: 130 }}
            />
          </div>

          <div style={{ fontSize: 12, opacity: 0.7 }}>
            Project: <strong>{project.project.name}</strong>
          </div>
        </div>
      </section>

      {/* KPIs */}
      <section style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(180px, 1fr))", gap: 12 }}>
        <KpiCard label="Project Stability Index" value={k?.projectStabilityIndex ?? "-"} />
        <KpiCard label="Critical Interfaces" value={k?.criticalInterfacesCount ?? "-"} />
        <KpiCard label="High Interfaces" value={k?.highInterfacesCount ?? "-"} />
        <KpiCard label="Orphan Requirements" value={k?.orphanRequirementsCount ?? "-"} />
      </section>

      {/* Alerts */}
      <section style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
        <h3 style={{ margin: "0 0 8px 0" }}>Top Alerts</h3>
        {k?.topAlerts?.length ? (
          <div style={{ display: "grid", gap: 10 }}>
            {k.topAlerts.map((a, idx) => (
              <div key={idx} style={{ border: "1px solid #eee", borderRadius: 8, padding: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <strong>{a.title}</strong>
                  <span style={{ fontSize: 12, opacity: 0.8 }}>{a.severity}</span>
                </div>
                <div style={{ marginTop: 6, fontSize: 13 }}>{a.why}</div>
                <div style={{ marginTop: 4, fontSize: 13, opacity: 0.85 }}>{a.impact}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, opacity: 0.7 }}>No alerts.</div>
        )}
      </section>

      {/* Tables */}
      <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <TableCard title="Planning (N2 windows)">
          <SimpleTable
            columns={[
              { key: "n2Id", label: "N2" },
              { key: "startMonth", label: "Start M" },
              { key: "endMonth", label: "End M" },
              { key: "startDate", label: "Start" },
              { key: "endDate", label: "End" },
            ]}
            rows={project.planning?.n2Windows || []}
          />
        </TableCard>

        <TableCard title="Top Interfaces">
          <SimpleTable
            columns={[
              { key: "id", label: "ID" },
              { key: "sourceN2", label: "Source" },
              { key: "targetN2", label: "Target" },
              { key: "type", label: "Type" },
              { key: "score", label: "Score" },
            ]}
            rows={k?.topInterfaces || []}
          />
        </TableCard>

        <TableCard title="Requirements">
          <SimpleTable
            columns={[
              { key: "id", label: "ID" },
              { key: "criticality", label: "Crit" },
              { key: "description", label: "Description" },
              { key: "linkedN2Text", label: "Linked N2" },
            ]}
            rows={(project.requirements || []).map((r) => ({
              ...r,
              linkedN2Text: (r.linkedN2 || []).join(", "),
            }))}
          />
        </TableCard>

        <TableCard title="Risks">
          <SimpleTable
            columns={[
              { key: "id", label: "ID" },
              { key: "severity", label: "Sev" },
              { key: "description", label: "Description" },
              { key: "linkedN2Text", label: "Linked N2" },
            ]}
            rows={(project.risks || []).map((r) => ({
              ...r,
              linkedN2Text: (r.linkedN2 || []).join(", "),
            }))}
          />
        </TableCard>
      </section>

      {/* Raw JSON (debug) */}
      <details style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
        <summary style={{ cursor: "pointer" }}>Raw ProjectInstance JSON</summary>
        <pre style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>
          {JSON.stringify(project, null, 2)}
        </pre>
      </details>
    </div>
  );
}

/* ---------- Small UI atoms (local) ---------- */

function KpiCard({ label, value }) {
  return (
    <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 12, opacity: 0.75 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginTop: 6 }}>{value}</div>
    </div>
  );
}

function TableCard({ title, children }) {
  return (
    <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, overflowX: "auto" }}>
      <h3 style={{ margin: "0 0 8px 0" }}>{title}</h3>
      {children}
    </div>
  );
}

function SimpleTable({ columns, rows }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c.key}
              style={{
                textAlign: "left",
                borderBottom: "1px solid #eee",
                padding: "8px 6px",
                fontSize: 12,
                opacity: 0.8,
              }}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(rows || []).map((r, idx) => (
          <tr key={idx}>
            {columns.map((c) => (
              <td key={c.key} style={{ borderBottom: "1px solid #f3f3f3", padding: "8px 6px" }}>
                {String(r?.[c.key] ?? "")}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}